import type { MouseEvent } from 'react'
import { trackEvent } from '../analytics'
import { BOOKING_URL, BOOK_INTRO, BRAND, DRAWING, EMAIL, HERO, HERO_HEADLINES } from '../data/content'

const INLET_STEP = 70

function InletGlyph({ id, x, y }: { id: string; x: number; y: number }) {
  if (id === 'advise') {
    return (
      <g transform={`translate(${x} ${y})`} aria-hidden="true">
        <rect x="0" y="0" width="28" height="18" className="fl-none st-ink" strokeWidth="1.5" />
        <path d="M6 18 L6 26 L14 18" className="fl-none st-ink" strokeWidth="1.5" strokeLinejoin="round" />
        <line x1="5" y1="7" x2="20" y2="7" className="st-faint" strokeWidth="1" />
        <line x1="5" y1="12" x2="14" y2="12" className="st-faint" strokeWidth="1" />
      </g>
    )
  }
  if (id === 'build') {
    return (
      <g transform={`translate(${x} ${y})`} aria-hidden="true">
        <rect x="0" y="0" width="30" height="8" className="fl-none st-ink" strokeWidth="1.5" />
        <rect x="0" y="12" width="30" height="8" className="fl-none st-ink" strokeWidth="1.5" />
        <rect x="0" y="24" width="30" height="6" className="fl-none st-acc" strokeWidth="1.5" />
      </g>
    )
  }
  return (
    <g transform={`translate(${x} ${y})`} aria-hidden="true">
      <circle cx="6" cy="8" r="4.5" className="fl-none st-ink" strokeWidth="1.5" />
      <circle cx="24" cy="8" r="4.5" className="fl-none st-ink" strokeWidth="1.5" />
      <circle cx="15" cy="24" r="4.5" className="fl-none st-acc" strokeWidth="1.5" />
      <line x1="10" y1="8" x2="19" y2="8" className="st-ink" strokeWidth="1" />
      <line x1="8" y1="12" x2="13" y2="20" className="st-ink" strokeWidth="1" />
      <line x1="22" y1="12" x2="17" y2="20" className="st-ink" strokeWidth="1" />
    </g>
  )
}

function Plate() {
  function onMove(event: MouseEvent<HTMLElement>) {
    const rect = event.currentTarget.getBoundingClientRect()
    event.currentTarget.style.setProperty('--gx', `${event.clientX - rect.left}px`)
    event.currentTarget.style.setProperty('--gy', `${event.clientY - rect.top}px`)
  }

  return (
    <figure className="plate-figure" onMouseMove={onMove}>
      <span className="plate-glow" aria-hidden="true" />
      <svg className="plate" viewBox="0 0 460 548" role="img" aria-labelledby="plate-title">
        <title id="plate-title">{DRAWING.accessibleName}</title>
        <defs>
          <pattern id="plate-hatch" width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
            <line x1="0" y1="0" x2="0" y2="6" stroke="currentColor" strokeWidth="1" opacity="0.28" />
          </pattern>
          <marker id="plate-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
            <path d="M0,1.4 L9,5 L0,8.6 z" fill="currentColor" />
          </marker>
        </defs>

        <g data-part="frame">
          <rect className="draw fl-none st-faint" x="4" y="4" width="452" height="540" strokeWidth="1" />
          <rect className="fl-none st-ink" x="14" y="14" width="432" height="520" strokeWidth="1.5" />
          <path d="M14 32 L14 14 L32 14" className="fl-none st-faint" strokeWidth="1" />
          <path d="M428 14 L446 14 L446 32" className="fl-none st-faint" strokeWidth="1" />
          <path d="M14 516 L14 534 L32 534" className="fl-none st-faint" strokeWidth="1" />
          <path d="M428 534 L446 534 L446 516" className="fl-none st-faint" strokeWidth="1" />
          <line x1="14" y1="456" x2="446" y2="456" className="st-ink" strokeWidth="1.5" />
          <g transform="translate(28 474)">
            <MarkPaths />
          </g>
          <text x="64" y="498" className="t-brand" fontSize="16">
            {BRAND}
          </text>
        </g>

        <g data-part="inlets">
          {DRAWING.inputs.map((input, index) => {
            const y = 30 + index * INLET_STEP
            const mid = y + 30
            return (
              <g key={input.id}>
                <rect x="28" y={y} width="318" height="60" className="fl-paper st-ink" strokeWidth="1.5" />
                <InletGlyph id={input.id} x={270} y={y + 15} />
                <text x="44" y={y + 26} className="t-brand" fontSize="17">
                  {input.name}
                </text>
                <text x="44" y={y + 46} className="t-faint" fontSize="12">
                  {input.detail}
                </text>
                <line
                  x1="346"
                  y1={mid}
                  x2="392"
                  y2={mid}
                  className="st-ink"
                  strokeWidth="1.5"
                  markerEnd="url(#plate-arrow)"
                />
              </g>
            )
          })}
          <line x1="392" y1="60" x2="392" y2="378" className="st-acc" strokeWidth="1.5" />
        </g>

        <g data-part="business">
          <rect x="28" y="246" width="404" height="192" className="fl-none st-acc" strokeWidth="2" />
          <text x="46" y="278" className="t-brand" fontSize="22">
            {DRAWING.business}
          </text>
          <path
            d="M52 420 L52 336 L112 300 L172 336 L172 420"
            className="fl-none st-ink"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <line x1="40" y1="420" x2="184" y2="420" className="st-ink" strokeWidth="1.5" />
          <rect x="104" y="380" width="24" height="40" className="fl-none st-ink" strokeWidth="1" />
          <rect x="68" y="348" width="18" height="14" className="fl-none st-ink" strokeWidth="1" />
          <rect x="132" y="348" width="18" height="14" className="fl-none st-ink" strokeWidth="1" />
          <line x1="210" y1="318" x2="392" y2="318" className="st-faint" strokeWidth="1" />
          <line x1="210" y1="348" x2="392" y2="348" className="st-faint" strokeWidth="1" />
          <line x1="210" y1="378" x2="392" y2="378" className="st-faint" strokeWidth="1" />
          <rect x="210" y="396" width="150" height="12" className="hatch" />
          <line x1="210" y1="396" x2="360" y2="396" className="st-ink" strokeWidth="1" />
          <line x1="210" y1="408" x2="360" y2="408" className="st-ink" strokeWidth="1" />
        </g>
      </svg>
      <figcaption className="plate-caption">{DRAWING.caption}</figcaption>
    </figure>
  )
}

function MarkPaths() {
  return (
    <>
      <path d="M4 9.5 L4 21 L22 21 L22 9.5" className="fl-none st-ink" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <path d="M4 9.5 L13 5 L22 9.5" className="fl-none st-ink" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <path d="M4 9.5 L13 14 L22 9.5" className="fl-none st-acc" strokeWidth="1" strokeLinejoin="round" strokeLinecap="round" />
      <path d="M13 14 L13 21.6" className="fl-none st-acc" strokeWidth="1" strokeLinecap="round" />
    </>
  )
}

export default function Hero() {
  return (
    <div className="stage">
      <div className="stage-pad">
        <div className="stage-grid" aria-hidden="true" />
        <div className="stage-light" aria-hidden="true" />
        <div className="container">
          <div className="stage-inner">
            <div className="beat--hero">
              <h1>
                {HERO_HEADLINES[0].split(/(?<=\.) /).map((sentence) => (
                  <span className="h1-line" key={sentence}>
                    {sentence}{' '}
                  </span>
                ))}
              </h1>
              <p className="hero-sentence">{HERO.sub}</p>
              <div className="hero-actions">
                <a
                  className="btn btn-primary hero-cta"
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('booking_click', { location: 'hero' })}
                >
                  {BOOK_INTRO}
                </a>
                <a className="tlink" href={`mailto:${EMAIL}`} onClick={() => trackEvent('email_click', { location: 'hero' })}>
                  {HERO.emailLabel}
                </a>
              </div>
            </div>
            <Plate />
          </div>
        </div>
      </div>
    </div>
  )
}
